import { useEffect, useRef } from 'react'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import config from 'constants/config'

function WrappedAudioPlayer() {
  const isMuted = useWrappedStorySelector(state => state.isMuted)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const audioRef = useRef<HTMLAudioElement>(null)

  // Start audio playing on mount (muted, so browser allows autoplay)
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.play().catch(() => {})
  }, [])

  // Handle mute state changes - also ensure playing when unmuting
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.muted = isMuted

    // When unmuting, ensure audio is playing (user gesture enables this)
    if (!isMuted && audio.paused) {
      audio.play().catch(() => {})
    }
  }, [isMuted])

  // Handle play/pause state changes
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPaused) {
      audio.pause()
    } else if (audio.paused) {
      audio.play().catch(() => {})
    }
  }, [isPaused])

  // Pause audio when browser tab is hidden (minimized or switched away)
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    let wasPlayingBeforeHidden = false

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasPlayingBeforeHidden = !audio.paused
        audio.pause()
      } else if (document.visibilityState === 'visible' && wasPlayingBeforeHidden) {
        // Resume only if audio was playing before tab was hidden
        audio.play().catch(() => {})
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  return (
    <audio
      ref={audioRef}
      src={config.WRAPPED_AUDIO_URL}
      loop
      muted={isMuted}
      preload="auto"
      style={{ display: 'none' }}
    />
  )
}

export default WrappedAudioPlayer
